import { createContext, useContext, useMemo } from 'react';
import { useCurrentFrame, useVideoConfig, type EffectsProp } from 'remotion';
import { DEFAULT_VIDEO_EFFECTS, hasAnyVideoEffects, normalizeVideoEffects, type VideoEffects } from '@/lib/video-effects/types';
import { buildEffectStack } from '@/lib/video-effects/buildEffectStack';
import { resolveActiveEffects } from '@/lib/video-effects/resolveActiveEffects';

type Ctx = { stack: EffectsProp; hasEffects: boolean; effects: VideoEffects };
const EMPTY_STACK: EffectsProp = [];
export const VideoEffectsContext = createContext<Ctx>({ stack: EMPTY_STACK, hasEffects: false, effects: DEFAULT_VIDEO_EFFECTS });

/** Mount at each composition ROOT: it reads the absolute frame, so per-interval
 *  <Sequence>s below cannot skew the section clock. `mapTime` translates the
 *  composition axis to the clip output timeline (unlocked full-video preview);
 *  null means "outside the clip" — sections do not apply. The composition
 *  aspect is the frame the footage is cover-fitted into, so pixel-sized params
 *  scale to the visible frame identically in export and the editor Player
 *  (post-layout cells and split panels use it as an approximation). */
export function VideoEffectsProvider({ value, mapTime, children }: {
  value?: unknown;
  mapTime?: (compositionSeconds: number) => number | null;
  children: React.ReactNode;
}) {
  const frame = useCurrentFrame();
  const { fps, width, height } = useVideoConfig();
  const frameAspect = width > 0 && height > 0 ? width / height : undefined;
  const effects = useMemo(() => normalizeVideoEffects(value), [value]);
  const hasEffects = useMemo(() => hasAnyVideoEffects(effects), [effects]);
  // Stable reference while no section is active, so consumers that redraw on
  // `[effects]` do not redraw every frame for base-look-only clips.
  // frameAspect is constant per composition, so this stays stable while playing.
  const baseStack = useMemo(() => buildEffectStack(effects, null, 0, frameAspect), [effects, frameAspect]);
  const raw = frame / fps;
  const timeSec = mapTime ? mapTime(raw) : raw;
  const sectionActive = resolveActiveEffects(effects.sections, timeSec).length > 0;
  const stack = useMemo(
    () => (sectionActive ? buildEffectStack(effects, timeSec, frame, frameAspect) : baseStack),
    [sectionActive, baseStack, effects, timeSec, frame, frameAspect],
  );
  const ctx = useMemo(() => ({ stack, hasEffects, effects }), [stack, hasEffects, effects]);
  return <VideoEffectsContext.Provider value={ctx}>{children}</VideoEffectsContext.Provider>;
}

/** The effect stack for the CURRENT frame (changes as sections start/end). */
export function useVideoEffectDescriptors(): EffectsProp {
  return useContext(VideoEffectsContext).stack;
}

/** Whether the clip has any effect anywhere — stable for the whole clip. Use it
 *  for CORS tagging, renderer lifetime and decoder-fallback decisions so
 *  crossing a section boundary never reloads media. */
export function useHasVideoEffects(): boolean {
  return useContext(VideoEffectsContext).hasEffects;
}
